import React, { useState, useEffect, useContext } from 'react';
import firebase from 'client/utils/firebase';

import { UserContext } from 'client/components/UserContext';
import { ServiceWorkerContext } from 'client/components/ServiceWorkerContext';
import errorHandler from 'client/utils/error-handler';

import WebPushContext from './WebPushContext';

const storage = window.localStorage;

const currentTokenKey = 'introwise-webpush-fcm-token';
const saveCurrentToken = (token: string) => storage.setItem(currentTokenKey, token);
const getCurrentToken = () => storage.getItem(currentTokenKey);
const deleteCurrentToken = () => storage.removeItem(currentTokenKey);

const dismissedKey = 'introwise-webpush-dismissed';
const saveDismissed = (dismissed: boolean) => storage.setItem(dismissedKey, dismissed.toString());
const getDismissed = () => storage.getItem(dismissedKey) === 'true';

const revokedKey = 'introwise-webpush-revoked';
const saveRevoked = (revoked: boolean) => storage.setItem(revokedKey, revoked.toString());
const getRevoked = () => storage.getItem(revokedKey) === 'true';

type Props = { children: React.ReactNode };

// Use default context for unsupported case
const WebPushUnsupportedProvider = ({ children }: Props) => <>{children}</>;

const WebPushSupportedProvider = ({ children }: Props) => {
  const [supported, setSupported] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [blocked, setBlocked] = useState(false);
  const [swUsed, setSwUsed] = useState(false);
  const [dismissed, setDismissed] = useState(getDismissed());
  const { userId, loggingIn } = useContext(UserContext);
  const { registration: swRegistration } = useContext(ServiceWorkerContext);

  const messaging = firebase.messaging();
  const deleteToken = (token: string) =>
    messaging
      .deleteToken(token)
      .then(() => deleteCurrentToken())
      .catch(error => errorHandler.report(error));

  const onTokenReceived = (token: string) => {
    setSupported(true);
    if (token) {
      if (userId) {
        // Logged in and enabled notifications
        setEnabled(true);
        setBlocked(false);

        // In addition to saving token remotely
        // we also save the token locally.
        // We want to avoid sending it multiple times if
        // it was already saved, to save write costs and avoid
        // contention
        const currentToken = getCurrentToken();
        if (currentToken !== token) {
          firebase
            .firestore()
            .collection('users')
            .doc(userId)
            .collection('internal')
            .doc('fcm')
            .set(
              {
                tokens: firebase.firestore.FieldValue.arrayUnion(token),
              },
              { merge: true },
            )
            .then(() => {
              saveCurrentToken(token);
            })
            .catch(error => errorHandler.report(error));
        }
      } else if (!loggingIn) {
        // Logged out, but enabled notifications before.
        // We delete registration token to prevent
        // receiving notifications
        deleteToken(token);
      }
    } else if (userId) {
      // Logged in, but didn't enable notifications
      // Do nothing, let them enable it through some UI flow
    }
  };

  const getToken = () => {
    if (getRevoked()) {
      // user revoked push permission
      // and we should not resubscribe
      setSupported(true);
      setBlocked(false);
      return;
    }
    messaging
      .getToken()
      .then(onTokenReceived)
      .catch(error => {
        setEnabled(false);
        if (error.code === 'messaging/notifications-blocked') {
          setSupported(true);
          setBlocked(true);
        } else {
          setSupported(false);
          console.error('Failed to retrieve web push token: ', error);
        }
      });
  };

  const requestPermission = () =>
    messaging.requestPermission().then(() => {
      setEnabled(true);
      setBlocked(false);
      getToken();
    });

  // Get initial token and subscribe to changes after the
  // service worker is registered.
  // Refresh the token on user change.
  useEffect(() => {
    if (!swRegistration) {
      return undefined;
    }
    if (!swUsed) {
      messaging.useServiceWorker(swRegistration);
      setSwUsed(true);
    }
    getToken();
    return messaging.onTokenRefresh(getToken);
  }, [userId, swRegistration]);

  const dismiss = () => {
    setDismissed(true);
    saveDismissed(true);
  };

  const enable = () => {
    saveRevoked(false);
    requestPermission();
  };
  const revoke = () => {
    dismiss();
    saveRevoked(true);
    setEnabled(false);
    messaging
      .getToken()
      .then(token => deleteToken(token))
      .catch(error => errorHandler.report(error));
  };

  return (
    <WebPushContext.Provider value={{ supported, enabled, blocked, dismissed, enable, revoke, dismiss }}>
      {children}
    </WebPushContext.Provider>
  );
};

const WebPushProvider = (props: Props) =>
  firebase.messaging.isSupported() && !!storage ? (
    <WebPushSupportedProvider {...props} />
  ) : (
    <WebPushUnsupportedProvider {...props} />
  );

export default WebPushProvider;
